import React from 'react';
import PropTypes from 'prop-types';
import { withStateHandlers, compose, lifecycle } from 'recompose';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';

import { withSearchHandler, withModalStateHandlers } from 'decorators';
import { Button } from 'components/core/Button';
import { ModalClose } from 'components/core/Modal';
import { Paragraph } from 'components/atoms';
import { calculateFilterCount } from 'utils/filterCount';
import './SearchFiltersTray.scss';

const allowedKeys = ['location', 'latitude', 'longitude', 'zip', 'city', 'state', 'subPageType', 'landingPageSize', 'neighborhood'];

export const enhance = compose(
	withSearchHandler,
	withModalStateHandlers,
	withStateHandlers(({ queryParams }) => ({ filterParams: queryParams, isScrolled: false }), {
		handleQueryParamChange: (state, props) => (params) => {
			// if on a large screen apply filters immediately
			if (props.isLarge) {
				props.onSearchParamChange(params);
			}

			return {
				filterParams: params,
			};
		},
		handleApplyFilters: (state, props) => () => {
			// Only close filters on mobile view
			if (!props.isLarge && props.closeFilters) {
				props.closeFilters();
			}
			props.onSearchParamChange(state.filterParams);
		},
		handleClearAll: (state, props) => () => {			
			const newParams = Object.keys(state.filterParams).reduce(
				(acc, key) => {
					acc[key] = allowedKeys.includes(key)
						? state.filterParams[key]
						: undefined;
					return acc;
				},
				{},
			);
			newParams.searchType = 'storage';
			props.onSearchParamChange(newParams);

			return { filterParams: newParams };
		},
		updateParams: () => (filterParams) => ({ filterParams }),
		setIsScrolled: () => (isScrolled) => ({ isScrolled }),
	}),
	lifecycle({
		componentDidUpdate(prevProps) {
			if (!isEqual(prevProps.queryParams, this.props.queryParams)) {
				this.props.updateParams(this.props.queryParams);
			}
		},
		componentDidMount() {
			this._handleScroll = throttle(() => {
				const isScrolled = window.scrollY > 0;
				if (this.props.isScrolled !== isScrolled) {
					this.props.setIsScrolled(isScrolled);
				}
			}, 100);
			window.addEventListener('scroll', this._handleScroll);
		},
		componentWillUnmount() {
			this._handleScroll.cancel();
			window.removeEventListener('scroll', this._handleScroll);
		},
	})
);

export function SearchFiltersTray({
	children,
	filtersShowing,
	handleQueryParamChange,
	handleClearAll,
	filterParams,
	handleApplyFilters,
	closeFilters,
	isLarge,
	isScrolled,
	filtersCount // Calculated by parent, in use on mobile due to "apply" button being required to update filters
}) {
	// utilized by desktop clearAll button as it doesn't require the "apply" button to change the filters
	const hasFiltersApplied = calculateFilterCount(filterParams, filterParams?.searchType) > 0;

	return (
		<div
			className={classnames('filters-tray', { showing: filtersShowing, scrolled: isScrolled })}
		>
			{!isLarge && closeFilters && (
				<div className="filters-tray-header">
					<h2 className="filters-tray-title">Filters</h2>
					{filtersCount > 0 && (
						<Button
							type="button"
							className="clearAll"
							link={true}
							gray={true}
							onClick={handleClearAll}
						>
							Clear All
						</Button>
					)}
					<ModalClose
						onClick={closeFilters}
						className="close-button-header"
					/>
				</div>
			)}
			{isLarge && (
				<div className="filter-header">
					<Paragraph
						size="standard"
						color="black"
						weight="ultraBold"
					>
						Filters
					</Paragraph>
					<Button
						type="button"
						className="clearAll"
						disabled={!filtersCount && !hasFiltersApplied}
						link={true}
						onClick={handleClearAll}
					>
						Clear All
					</Button>
				</div>
			)}
			<div className="filters-tray-content">
				{React.cloneElement(children, {
					queryParams: filterParams,
					onFilter: handleQueryParamChange,
				})}
			</div>
			<div className={classnames('apply-all-filters')}>
				<Button onClick={handleApplyFilters}>Apply Filters</Button>
			</div>
		</div>
	);
}

SearchFiltersTray.propTypes = {
	filtersShowing: PropTypes.bool,
	children: PropTypes.node.isRequired,
	filterParams: PropTypes.object,
	handleQueryParamChange: PropTypes.func,
	handleClearAll: PropTypes.func,
	handleApplyFilters: PropTypes.func.isRequired,
	closeFilters: PropTypes.func,
	isLarge: PropTypes.bool,
	isScrolled: PropTypes.bool,
	filtersCount: PropTypes.number,
};

export default enhance(SearchFiltersTray);
